import React from "react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Settings2,
  Trash2,
  Edit2,
  CircleAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import ColorSwatch from "../RightStyle/TailwindStylingPanel/components/input/ColorSwatch";
import useLayoutStore from "@/store/zustand/layoutStore";
import { type RootState, useTypedSelector } from "@/store/store";
import type {
  ColorGroup as ColorStoreGroup,
  ColorToken,
} from "@/components/DesignTokens/types";
import {
  COLOUR_FALLBACK,
  getDisplayColor,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";
import { EditColorPopover, NewColorPopover } from "./components/ColorPopover";
import { ColorItemContextMenu } from "./components/ColorItemContextMenu";
import type { ColorGroup, ColorGroup as DesignTokenGroup } from "./types";
import { useDesignTokens } from "./hooks/useDesignTokens";
import { useColorGroupEditing } from "./hooks/useColorGroupEditing";
import { useDesignTokensStore } from "./store/designTokensStore";
import { toast } from "sonner";
import {
  checkColorExists,
  checkUngroupedMergeConflict,
  validateColorName,
  validateGroupName,
} from "./utils/validation";
import { useDarkModeStore } from "@/store/zustand/darkModeStore";
import { DesignTokensSkeleton } from "./components/DesignTokensSkeleton";
import { DesignTokensPanelHeader } from "./components/DesignTokensPanelHeader";

interface ColorsTabContentProps {
  hasAnyColors: boolean;
  shouldShowDarkModeGenerate: boolean;
  shouldShowTryAgain: boolean;
  searchQuery: string;
  hasSearchResults: boolean;
  loading: boolean;
  filteredUngroupedColors: ColorToken[];
  filteredColorGroups: ColorStoreGroup[];
  getDisplayColor: (
    color: ColorToken,
    cssCustomProperties?: {
      root: Record<string, string>;
      dark: Record<string, string>;
    } | null,
    comboboxOptions?: Array<{ tailwind: string; css: string }>,
  ) => string;
  cssCustomProperties: {
    root: Record<string, string>;
    dark: Record<string, string>;
  } | null;
  expandedGroups: Set<string>;
  colorGroupEditing: ReturnType<typeof useColorGroupEditing>;
  colorInputRefMap: React.MutableRefObject<Map<string, HTMLInputElement>>;
  onRefreshColors: () => void;
  onOpenEditPopover: (color: ColorToken, event?: React.MouseEvent) => void;
  onDeleteColor: (groupName: string, colorName: string) => Promise<void>;
  onToggleGroup: (groupName: string) => void;
  onHandleRenameGroup: (oldName: string, newName: string) => void;
  onHandleDeleteGroup: (groupName: string) => void;
  onAddColorToGroup: (groupName: string) => void;
  onSaveColorName: () => Promise<void>;
  onSaveGroupName: () => Promise<void>;
}

const ColorsTabContent: React.FC<ColorsTabContentProps> = ({
  hasAnyColors,
  shouldShowTryAgain,
  searchQuery,
  hasSearchResults,
  loading,
  filteredUngroupedColors,
  filteredColorGroups,
  getDisplayColor,
  cssCustomProperties,
  expandedGroups,
  colorGroupEditing,
  colorInputRefMap,
  onRefreshColors,
  onOpenEditPopover,
  onDeleteColor,
  onToggleGroup,
  onHandleRenameGroup,
  onHandleDeleteGroup,
  onAddColorToGroup,
  onSaveColorName,
  onSaveGroupName,
}) => {
  // Show search no results first
  if (searchQuery && !hasSearchResults) {
    return (
      <div className="space-y-2 min-w-0">
        <div className="p-4 text-center">
          <div className="text-[#808086] text-sm">
            No results found for "{searchQuery}"
          </div>
        </div>
      </div>
    );
  }

  // Show no colors state
  if (!hasAnyColors) {
    return (
      <div className="space-y-2 min-w-0">
        <div className="p-4 text-center">
          <div className="text-[#808086] text-sm">
            No custom colors found in your Tailwind configuration.
          </div>
          <div className="text-[#606060] text-xs mt-1">
            Add custom colors to your tailwind.config.js to see them here.
          </div>
          {shouldShowTryAgain && (
            <button
              type="button"
              onClick={onRefreshColors}
              disabled={loading}
              className="mt-3 px-3 py-1.5 text-xs bg-primary-0 text-text-primary rounded-md hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Refreshing..." : "Try Again"}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show the actual content
  return (
    <div className="space-y-2 min-w-0">
      {/* Render ungrouped colors first */}
      {filteredUngroupedColors.length > 0 && (
        <div className="min-w-0">
          <div className="space-y-1 min-w-0">
            {filteredUngroupedColors.map((color) => (
              <DesignTokenColorItem
                key={color.tailwindClass}
                color={color}
                getDisplayColor={getDisplayColor}
                cssCustomProperties={cssCustomProperties || undefined}
                onEdit={onOpenEditPopover}
                onDelete={onDeleteColor}
                editingColorNameId={colorGroupEditing.editingColorNameId}
                tempColorName={colorGroupEditing.tempColorName}
                onStartEditingColorName={
                  colorGroupEditing.startEditingColorName
                }
                onCancelEditingColorName={
                  colorGroupEditing.cancelEditingColorName
                }
                onSaveColorName={onSaveColorName}
                onColorNameChange={colorGroupEditing.setTempColorName}
                onColorNameEditKeyDown={
                  colorGroupEditing.handleColorNameEditKeyDown
                }
                colorInputRefMap={colorInputRefMap}
              />
            ))}
          </div>
          {/* Add separator if there are grouped colors after ungrouped ones */}
          {filteredColorGroups.length > 0 && (
            <div className="border-b border-[#2f3035] my-3" />
          )}
        </div>
      )}

      {/* Render grouped colors */}
      {filteredColorGroups.map((group) => (
        <DesignTokenColorGroup
          key={group.name}
          group={group}
          getDisplayColor={getDisplayColor}
          cssCustomProperties={cssCustomProperties || undefined}
          expandedGroups={expandedGroups}
          toggleGroup={onToggleGroup}
          handleRenameGroup={onHandleRenameGroup}
          handleDeleteGroup={onHandleDeleteGroup}
          onEditColor={onOpenEditPopover}
          onDeleteColor={onDeleteColor}
          onAddColor={onAddColorToGroup}
          editingGroupName={colorGroupEditing.editingGroupName}
          tempGroupName={colorGroupEditing.tempGroupName}
          onStartEditingGroup={colorGroupEditing.startEditingGroup}
          onCancelEditingGroup={colorGroupEditing.cancelEditingGroup}
          onSaveGroupName={onSaveGroupName}
          onGroupNameChange={colorGroupEditing.setTempGroupName}
          onGroupNameKeyDown={colorGroupEditing.handleGroupNameKeyDown}
          addingColorToGroup={null}
          newColorName=""
          editingColorNameId={colorGroupEditing.editingColorNameId}
          tempColorName={colorGroupEditing.tempColorName}
          onStartEditingColorName={colorGroupEditing.startEditingColorName}
          onCancelEditingColorName={colorGroupEditing.cancelEditingColorName}
          onSaveColorName={onSaveColorName}
          onColorNameChange={colorGroupEditing.setTempColorName}
          onColorNameEditKeyDown={colorGroupEditing.handleColorNameEditKeyDown}
          colorInputRefMap={colorInputRefMap}
        />
      ))}
    </div>
  );
};

/**
 * Essential Tailwind color classes that should not be renamed.
 * otherwise, it will break the dev server since tailwind refuses to load
 */
const PROTECTED_COLOR_CLASSES = [
  // Core semantic colors
  "background",
  "foreground",
  "border",
  "input",
  "ring",
];

// only apply protections to ungrouped colors
const isProtectedColorClass = (colorName: string): boolean => {
  // Check exact match for top-level colors
  return PROTECTED_COLOR_CLASSES.includes(colorName.toLowerCase());
};

// Get protection reason for tooltip
const getProtectionReason = (colorName: string): string => {
  if (PROTECTED_COLOR_CLASSES.includes(colorName)) {
    return "This is a core Tailwind color class and cannot be renamed";
  }

  return "This color class cannot be renamed";
};

interface DesignTokensPanelProps {
  className?: string;
}

export const DesignTokensPanel = ({
  className,
}: DesignTokensPanelProps = {}) => {
  const activeTab = useDesignTokensStore((state) => state.activeTab);
  const designTokensSearchQuery = useDesignTokensStore(
    (state) => state.searchQuery,
  );
  const setDesignTokensSearchQuery = useDesignTokensStore(
    (state) => state.setSearchQuery,
  );
  const expandedGroups = useDesignTokensStore((state) => state.expandedGroups);
  const setExpandedGroups = useDesignTokensStore(
    (state) => state.setExpandedGroups,
  );
  const storeToggleGroup = useDesignTokensStore((state) => state.toggleGroup);
  const expandGroup = useDesignTokensStore((state) => state.expandGroup);
  const updateExpandedGroupsOnDataChange = useDesignTokensStore(
    (state) => state.updateExpandedGroupsOnDataChange,
  );
  const mergedColorGroups = useDesignTokensStore(
    (state) => state.mergedColorGroups,
  );
  const mergedColors = useDesignTokensStore((state) => state.mergedColors);
  const loading = useDesignTokensStore((state) => state.loading);
  const lightColorGroups = useDesignTokensStore(
    (state) => state.lightColorGroups,
  );
  const lightColors = useDesignTokensStore((state) => state.lightColors);
  const darkColorGroups = useDesignTokensStore(
    (state) => state.darkColorGroups,
  );
  const darkColors = useDesignTokensStore((state) => state.darkColors);

  // Local state for popovers (will migrate to store later)
  const [isEditPopoverOpen, setIsEditPopoverOpen] = useState(false);
  const selectedColorForEditRef = useRef<ColorToken | undefined>(undefined);
  const lightColorTokenRef = useRef<ColorToken | undefined>(undefined);
  const darkColorTokenRef = useRef<ColorToken | undefined>(undefined);
  const editingColorNameRef = useRef<string>("");
  const selectedColorIdRef = useRef<string | null>(null);
  const [editingColorName, setEditingColorName] = useState("");
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [isNewColorPopoverOpen, setIsNewColorPopoverOpen] = useState(false);
  const [newColorGroupName, setNewColorGroupName] = useState("");
  const [newColorName, setNewColorName] = useState("");
  const [newColorValue, setNewColorValue] = useState(COLOUR_FALLBACK);
  const [savingColor, setSavingColor] = useState<boolean>(false);

  // Create stable getters that don't cause re-renders
  const selectedColorForEdit = selectedColorForEditRef.current;
  const lightColorToken = lightColorTokenRef.current;
  const darkColorToken = darkColorTokenRef.current;
  const selectedColorId = selectedColorIdRef.current;

  // Refs for DOM elements and focus management
  const panelRef = useRef<HTMLDivElement>(null);
  const colorInputRefMap = useRef<Map<string, HTMLInputElement>>(new Map());

  // Track currently focused input to restore focus after network requests
  const focusedInputRef = useRef<{
    type: "group" | "color";
    id: string;
  } | null>(null);

  const {
    addColor,
    updateColor,
    deleteColor,
    deleteColorGroup,
    renameGroup,
    refreshColors,
  } = useDesignTokens();

  const hasAnyColors =
    lightColors.length > 0 ||
    lightColorGroups.length > 0 ||
    darkColors.length > 0 ||
    darkColorGroups.length > 0;

  const devServerStatus = useTypedSelector(
    (state: RootState) => state.codebase.devServerStatus,
  );

  // Read from the canvas-specific dark mode state, not the global one
  const isCanvasDarkMode = useDarkModeStore((state) => state.isCanvasDarkMode);

  const cssCustomProperties = useTypedSelector(
    (state) => state.iframe.cssCustomProperties,
  );

  // Layout store for collapsing the right panel
  const collapseRight = useLayoutStore((state) => state.collapseRight);

  // Use the color group editing hook with our wrapped functions and color data
  // expandGroup is now provided by the store

  // Validation functions for the hook
  const validateGroupName = useCallback(
    (groupName: string, excludeGroupName?: string) => {
      // Use existing validation utility
      const basicValidation = validateGroupName(groupName);
      if (!basicValidation.valid) {
        return basicValidation;
      }

      // Check if group already exists
      if (mergedColorGroups) {
        const existingGroup = mergedColorGroups.find(
          (group) =>
            group.name.toLowerCase() === groupName.toLowerCase() &&
            group.name !== excludeGroupName,
        );
        if (existingGroup) {
          return { valid: false, error: `Group "${groupName}" already exists` };
        }
      }

      // Check if an ungrouped color with this name exists
      if (mergedColors) {
        const existingUngroupedColor = mergedColors.find(
          (color) => color.name.toLowerCase() === groupName.toLowerCase(),
        );
        if (existingUngroupedColor) {
          return {
            valid: false,
            error: `An ungrouped color "${groupName}" already exists. Choose a different group name.`,
          };
        }
      }

      return { valid: true };
    },
    [mergedColorGroups, mergedColors],
  );

  const validateColorName = useCallback(
    (colorName: string, groupName?: string, excludeColorId?: string) => {
      // Parse color name if it contains slash
      const parseColorName = (fullName: string) => {
        const parts = fullName.split("/");
        if (parts.length === 2) {
          return {
            groupName: parts[0].trim(),
            colorName: parts[1].trim(),
          };
        }
        return {
          groupName: null,
          colorName: fullName.trim(),
        };
      };

      const { groupName: parsedGroupName, colorName: parsedColorName } =
        parseColorName(colorName);
      const targetGroupName = parsedGroupName || groupName;
      const targetColorName = parsedColorName;

      // Use existing validation utility
      const basicValidation = validateColorName(targetColorName);
      if (!basicValidation.valid) {
        return basicValidation;
      }

      // Check for duplicates
      if (mergedColorGroups && mergedColors) {
        // Check ungrouped colors
        if (!targetGroupName || targetGroupName === targetColorName) {
          const existingUngrouped = mergedColors.find(
            (c) => c.name === targetColorName && c.name !== excludeColorId,
          );
          if (existingUngrouped) {
            return {
              valid: false,
              error: `Color "${targetColorName}" already exists in ungrouped colors`,
            };
          }
        }

        // Check grouped colors
        if (targetGroupName && targetGroupName !== targetColorName) {
          const targetGroup = mergedColorGroups.find(
            (g) => g.name === targetGroupName,
          );
          if (targetGroup) {
            const existingInGroup = targetGroup.colors.find(
              (c) =>
                c.name === targetColorName &&
                `${targetGroupName}/${c.name}` !== excludeColorId,
            );
            if (existingInGroup) {
              return {
                valid: false,
                error: `Color "${targetColorName}" already exists in group "${targetGroupName}"`,
              };
            }
          }
        }

        // Check if creating a new group that conflicts with existing ungrouped colors
        if (parsedGroupName) {
          const existingUngroupedWithGroupName = mergedColors.find(
            (color) =>
              color.name.toLowerCase() === parsedGroupName.toLowerCase(),
          );
          if (existingUngroupedWithGroupName) {
            return {
              valid: false,
              error: `Cannot create group "${parsedGroupName}": an ungrouped color with this name already exists`,
            };
          }
        }

        // Additional validation: check if moving to a group that already has a color with the same name as the group
        if (parsedGroupName && parsedColorName === parsedGroupName) {
          return {
            valid: false,
            error: "Color name cannot be the same as the group name",
          };
        }
      }

      return { valid: true };
    },
    [mergedColorGroups, mergedColors],
  );

  const colorGroupEditing = useColorGroupEditing(
    updateColor,
    deleteColor,
    addColor,
    expandGroup,
    renameGroup,
    validateGroupName,
    validateColorName,
  );

  const colorGroups = mergedColorGroups;
  const ungroupedColors = mergedColors;

  // Get iframe state to determine if canvas is loaded
  const startTimeRef = useRef<number | null>(Date.now());

  // Check if show the try again state
  const shouldShowTryAgainState =
    startTimeRef.current !== null && Date.now() - startTimeRef.current > 30000; // only show try again state after 30 seconds

  // Expand all groups by default when colors are loaded, and sync with backend expansion state
  const initializedExpandedRef = useRef(false);
  useEffect(() => {
    if (mergedColorGroups.length === 0 && mergedColors.length === 0) {
      return;
    }

    // Initial mount: expand all current groups once
    if (!initializedExpandedRef.current) {
      const allGroupNames = new Set(
        mergedColorGroups.map((group) => group.name),
      );
      setExpandedGroups(allGroupNames);
      initializedExpandedRef.current = true;
      return;
    }

    // Subsequent updates: use store method for complex updates
    updateExpandedGroupsOnDataChange(
      mergedColorGroups.map((g) => g.name),
      mergedColorGroups,
    );
  }, [
    mergedColorGroups,
    mergedColors,
    updateExpandedGroupsOnDataChange,
    setExpandedGroups,
  ]);

  // Focus management for group name editing
  useEffect(() => {
    if (colorGroupEditing.editingGroupName) {
      focusedInputRef.current = {
        type: "group",
        id: colorGroupEditing.editingGroupName,
      };
      // Use requestAnimationFrame to ensure the DOM has updated after context menu closes
      requestAnimationFrame(() => {
        // Find the input with the specific data attribute for the editing group
        const input = document.querySelector(
          `input[data-editing-group="${colorGroupEditing.editingGroupName}"]`,
        ) as HTMLInputElement;
        if (input) {
          input.focus();
          // Select all text for easy editing
          input.select();
        }
      });
    } else {
      focusedInputRef.current = null;
    }
  }, [colorGroupEditing.editingGroupName]);

  // Focus management for color name editing using ref map
  useEffect(() => {
    const editingId = colorGroupEditing.editingColorNameId;
    if (editingId) {
      focusedInputRef.current = { type: "color", id: editingId };
      // Use requestAnimationFrame to ensure the DOM has updated after context menu closes
      requestAnimationFrame(() => {
        const input = colorInputRefMap.current.get(editingId);
        if (input) {
          input.focus();
          // Select all text for easy editing
          input.select();
        }
      });
    } else {
      focusedInputRef.current = null;
    }
  }, [colorGroupEditing.editingColorNameId]);

  // Additional focus restoration on every render when editing
  useEffect(() => {
    const focusedInput = focusedInputRef.current;
    if (
      focusedInput &&
      (colorGroupEditing.editingGroupName ||
        colorGroupEditing.editingColorNameId)
    ) {
      // Check if the currently focused element is not our input
      const activeElement = document.activeElement;
      const isInputFocused =
        activeElement &&
        ((focusedInput.type === "group" &&
          activeElement.getAttribute("data-editing-group") ===
            focusedInput.id) ||
          (focusedInput.type === "color" &&
            colorInputRefMap.current.get(focusedInput.id) === activeElement));

      if (!isInputFocused) {
        // Use requestAnimationFrame to restore focus after the current render cycle
        requestAnimationFrame(() => {
          if (focusedInput.type === "group") {
            const input = document.querySelector(
              `input[data-editing-group="${focusedInput.id}"]`,
            ) as HTMLInputElement;
            if (input) {
              input.focus();
              input.select();
            }
          } else if (focusedInput.type === "color") {
            const input = colorInputRefMap.current.get(focusedInput.id);
            if (input) {
              input.focus();
              input.select();
            }
          }
        });
      }
    }
  });

  // Use search query for filtering (filtering is now done in render)
  const searchQuery = designTokensSearchQuery;

  const toggleGroup = (groupName: string) => {
    storeToggleGroup(groupName);
  };

  const handleRenameGroup = async (oldName: string, newName: string) => {
    if (oldName === newName || !newName.trim()) return;

    // Validate group name
    const validation = validateGroupName(newName);
    if (!validation.valid) {
      toast.error(`Invalid group name: ${validation.error}`);
      return;
    }

    // Check if group name already exists
    const existingGroup = mergedColorGroups.find(
      (group) =>
        group.name.toLowerCase() === newName.toLowerCase() &&
        group.name !== oldName,
    );
    if (existingGroup) {
      toast.error(`Group "${newName}" already exists`);
      return;
    }

    // Check if an ungrouped color with this name exists
    const existingUngroupedColor = mergedColors.find(
      (color) => color.name.toLowerCase() === newName.toLowerCase(),
    );
    if (existingUngroupedColor) {
      toast.error(
        `An ungrouped color "${newName}" already exists. Choose a different group name.`,
      );
      return;
    }

    try {
      // Transfer expansion state from old name to new name
      const wasExpanded = expandedGroups.has(oldName);

      await renameGroup(oldName, newName);

      // Ensure the renamed group maintains its expansion state
      if (wasExpanded) {
        const newSet = new Set(expandedGroups);
        newSet.delete(oldName); // Remove old name
        newSet.add(newName); // Add new name
        setExpandedGroups(newSet);
      }
    } catch (error) {
      console.error("Failed to rename group:", error);
      toast.error("Failed to rename group. Please try again.");
    }
  };

  const handleDeleteGroup = async (groupName: string) => {
    try {
      await deleteColorGroup(groupName);
    } catch (error) {
      console.error("Failed to delete group:", error);
      toast.error("Failed to delete group. Please try again.");
    }
  };

  const addNewColor = () => {
    // Open popover to create an ungrouped color by default
    setNewColorGroupName(""); // Empty means ungrouped
    setNewColorName("");
    setNewColorValue(COLOUR_FALLBACK);
    setIsNewColorPopoverOpen(true);
  };

  const addColorToGroup = (groupName: string) => {
    setNewColorGroupName(groupName);
    setNewColorName("");
    setNewColorValue(COLOUR_FALLBACK);
    setIsNewColorPopoverOpen(true);
  };

  const saveGroupName = async () => {
    try {
      await colorGroupEditing.saveGroupName();
    } catch (error) {
      console.error("Failed to save group name:", error);
      toast.error("Failed to save group name. Please try again.");
    }
  };

  const saveColorName = async () => {
    try {
      // Get the current color being edited and the new name
      const editingColorId = colorGroupEditing.editingColorNameId;
      const newColorName = colorGroupEditing.tempColorName;

      if (!editingColorId || !newColorName) {
        return;
      }

      // Find the original color to get its current name and group
      const findOriginalColor = () => {
        // Search in ungrouped colors
        for (const color of mergedColors) {
          if (
            color.name === editingColorId ||
            color.tailwindClass === editingColorId
          ) {
            return { color, isGrouped: false };
          }
        }

        // Search in grouped colors
        for (const group of mergedColorGroups) {
          for (const color of group.colors) {
            const colorPath = `${group.name}/${color.name}`;
            if (colorPath === editingColorId || color.name === editingColorId) {
              return { color, isGrouped: true, groupName: group.name };
            }
          }
        }
        return null;
      };

      const originalColorData = findOriginalColor();
      if (!originalColorData) {
        toast.error("Could not find the color being edited");
        return;
      }

      const { color: originalColor, isGrouped, groupName } = originalColorData;

      // Skip validation if the name hasn't changed
      if (originalColor.name === newColorName.trim()) {
        await colorGroupEditing.saveColorName();
        return;
      }

      // Validate color name
      const validation = validateColorName(newColorName);
      if (!validation.valid) {
        toast.error(`Invalid color name: ${validation.error}`);
        return;
      }

      // Parse the new name to check if it includes group syntax (group/color)
      const newNameParts = newColorName.trim().split("/");
      const isNewNameGrouped = newNameParts.length === 2;
      const targetGroupName = isNewNameGrouped
        ? newNameParts[0]
        : isGrouped
          ? groupName || null
          : null;
      const targetColorName = isNewNameGrouped
        ? newNameParts[1]
        : newColorName.trim();

      // Check for duplicates using the existing validation function
      const excludeColorId =
        isGrouped && groupName
          ? `${groupName}/${originalColor.name}`
          : originalColor.name;
      const { exists, location } = checkColorExists(
        targetColorName,
        targetGroupName,
        mergedColorGroups,
        mergedColors,
        excludeColorId,
      );

      if (exists) {
        toast.error(`Color "${targetColorName}" already exists in ${location}`);
        return;
      }

      // Additional validation: check if moving to a group that already has a color with the same name as the group
      if (
        isNewNameGrouped &&
        targetGroupName &&
        targetColorName === targetGroupName
      ) {
        toast.error("Color name cannot be the same as the group name");
        return;
      }

      // Check if creating a new group that conflicts with existing ungrouped colors
      if (isNewNameGrouped && targetGroupName) {
        const existingUngroupedWithGroupName = mergedColors.find(
          (color) => color.name.toLowerCase() === targetGroupName.toLowerCase(),
        );
        if (existingUngroupedWithGroupName) {
          toast.error(
            `Cannot create group "${targetGroupName}": an ungrouped color with this name already exists`,
          );
          return;
        }
      }

      await colorGroupEditing.saveColorName();
    } catch (error) {
      console.error("Failed to save color name:", error);
      toast.error("Failed to save color name. Please try again.");
    }
  };

  // EditColorPopover handlers
  const openEditPopover = (color: ColorToken, event?: React.MouseEvent) => {
    // Capture mouse position if event is provided
    if (event) {
      setMousePosition({ x: event.clientX, y: event.clientY });
    }

    // Try case insensitive matches

    let lightColorToken: ColorToken | undefined;
    let darkColorToken: ColorToken | undefined;
    // Helper function to search both ungrouped colors and colors within groups
    const findMatchingColor = (
      ungroupedArray: ColorToken[],
      groupArray: ColorGroup[],
      targetName: string,
      targetGroup: string | null,
    ) => {
      // If no group specified, search in ungrouped colors
      if (!targetGroup) {
        const match = ungroupedArray.find((c) => {
          const nameMatch = c.name?.toLowerCase() === targetName?.toLowerCase();
          const groupMatch = c.group === null || c.group === undefined;
          return nameMatch && groupMatch;
        });
        return match;
      }

      // If group specified, search in grouped colors
      const targetColorGroup = groupArray.find(
        (g) => g.name?.toLowerCase() === targetGroup?.toLowerCase(),
      );

      if (targetColorGroup) {
        const match = targetColorGroup.colors.find((c) => {
          const nameMatch = c.name?.toLowerCase() === targetName?.toLowerCase();
          return nameMatch;
        });
        return match;
      }

      return undefined;
    };

    // Since we're using merged groups, we need to determine which token we actually clicked on
    // by looking in both light and dark collections

    // Look for the color in light tokens first
    const lightColor = findMatchingColor(
      lightColors,
      lightColorGroups,
      color.name,
      color.group,
    );

    // Look for the color in dark tokens
    const darkColor = findMatchingColor(
      darkColors,
      darkColorGroups,
      color.name,
      color.group,
    );

    // Set up the light token if it exists
    if (lightColor) {
      const lightColorTokenData = lightColor as ColorToken;
      lightColorToken = {
        name: lightColorTokenData.name,
        group: lightColorTokenData.group || null,
        value: lightColorTokenData.color, // Use resolved color, not CSS variable
        color: lightColorTokenData.color,
        tailwindClass: lightColorTokenData.tailwindClass,
        hexColor: lightColorTokenData.hexColor,
      };
    } else {
      lightColorToken = undefined;
    }

    // Set up the dark token if it exists
    if (darkColor) {
      const darkColorTokenData = darkColor as ColorToken;
      darkColorToken = {
        name: darkColorTokenData.name,
        group: darkColorTokenData.group || null,
        value: darkColorTokenData.color,
        color: darkColorTokenData.color,
        tailwindClass: darkColorTokenData.tailwindClass,
        hexColor: darkColorTokenData.hexColor,
      };
    } else {
      darkColorToken = undefined;
    }

    // Use the dark token as selectedColorForEdit when light token is missing
    selectedColorForEditRef.current = lightColorToken || darkColorToken;
    lightColorTokenRef.current = lightColorToken;
    darkColorTokenRef.current = darkColorToken;
    // Use the proper color path for grouped colors, fallback to name for ungrouped
    selectedColorIdRef.current = color.group
      ? `${color.group}/${color.name}`
      : color.name;
    editingColorNameRef.current = color.name;
    setEditingColorName(color.name);

    setIsEditPopoverOpen(true);
  };

  const closeEditPopover = () => {
    setIsEditPopoverOpen(false);
    selectedColorForEditRef.current = undefined;
    lightColorTokenRef.current = undefined;
    darkColorTokenRef.current = undefined;
    selectedColorIdRef.current = null;
    editingColorNameRef.current = "";
    setEditingColorName("");
    setMousePosition(null);
  };

  const handleColorNameChange = (name: string) => {
    setEditingColorName(name);
    editingColorNameRef.current = name;
  };

  const handleColorValueChange = (colorId: string, newColor: string) => {
    if (selectedColorForEditRef.current) {
      selectedColorForEditRef.current = {
        ...selectedColorForEditRef.current,
        color: newColor,
        value: newColor,
      };
    }
  };

  const handleSaveColorFromPicker = async (isDark?: boolean) => {
    if (savingColor) return;
    setSavingColor(true);
    if (selectedColorId && selectedColorForEditRef.current) {
      try {
        // Parse group and color name from the editing name (supports group/color format)
        const parseColorName = (fullName: string) => {
          const parts = fullName.split("/");
          if (parts.length === 2) {
            return {
              groupName: parts[0].trim(),
              colorName: parts[1].trim(),
            };
          }
          return {
            groupName: null,
            colorName: fullName.trim(),
          };
        };

        // Find the original group and color name from the colorId path
        // Path is now in forward slash format from backend
        const normalizedSelectedColorId = selectedColorId;
        const pathParts = normalizedSelectedColorId.split("/");

        let originalGroupName: string;
        let originalColorName: string;

        if (pathParts.length >= 2) {
          // Grouped color: "groupName/colorName"
          originalGroupName = pathParts[0];
          originalColorName = pathParts[pathParts.length - 1];
        } else {
          // Ungrouped color: the group name is the same as the color name
          originalGroupName = pathParts[0];
          originalColorName = pathParts[0];
        }

        const { groupName: newGroupName, colorName: newColorName } =
          parseColorName(editingColorName);

        // If user specified a new group (group/color format), we need to:
        // 1. Delete the color from the old group
        // 2. Add the color to the new group
        // This handles both cross-group moves AND ungrouped -> grouped conversions
        const isUngroupedToGrouped = newGroupName && pathParts.length === 1; // Original was ungrouped
        const isCrossGroupMove =
          newGroupName && newGroupName !== originalGroupName;

        // Only pass the new name if it's actually different from the original
        const finalNewName =
          newColorName !== originalColorName
            ? isUngroupedToGrouped || isCrossGroupMove
              ? editingColorName
              : newColorName
            : undefined;

        // Get the original color value to compare with current
        const originalColor =
          lightColorToken?.hexColor || lightColorToken?.color;
        const currentColor = selectedColorForEditRef.current.color;

        // If neither name nor color has changed, skip the API call
        if (!finalNewName && originalColor === currentColor) {
          closeEditPopover();
          return;
        }

        // Check for duplicate colors when renaming
        if (finalNewName && finalNewName !== originalColorName) {
          const targetGroupName = newGroupName || originalGroupName;
          const { exists, location } = checkColorExists(
            finalNewName,
            targetGroupName === finalNewName ? null : targetGroupName, // null for ungrouped colors
            mergedColorGroups,
            mergedColors,
          );

          if (exists) {
            toast.error(
              `Color "${finalNewName}" already exists in ${location}`,
            );
            return;
          }

          // Check for ungrouped color merge conflicts when moving to ungrouped
          const isMovingToUngrouped = !newGroupName;
          if (isMovingToUngrouped) {
            const { willMerge, existingGroup } = checkUngroupedMergeConflict(
              finalNewName,
              mergedColorGroups,
            );

            if (willMerge && existingGroup) {
              // Show informative toast about the merge behavior
              toast.info(
                `Color "${finalNewName}" will be added to the existing "${existingGroup.name}" group (${existingGroup.colors.length} colors) as "${finalNewName}/${finalNewName}"`,
              );
            }
          }
        }

        await updateColor(
          originalGroupName,
          originalColorName,
          finalNewName,
          selectedColorForEditRef.current.color,
          isDark, // Pass isDark from ColorPopover's view mode
        );

        // If it's a cross-group move, expand the target group so user can see the moved color
        if ((isUngroupedToGrouped || isCrossGroupMove) && newGroupName) {
          expandGroup(newGroupName);
        }

        closeEditPopover();
      } catch (error) {
        console.error("Failed to update color:", error);
        toast.error("Failed to update color. Please try again.");
      } finally {
        setSavingColor(false);
      }
    }
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    // Handle escape key to close popover
    if (e.key === "Escape") {
      closeEditPopover();
    }
  };

  // NewColorPopover handlers
  const closeNewColorPopover = () => {
    setIsNewColorPopoverOpen(false);
    setNewColorGroupName("");
    setNewColorName("");
    setNewColorValue(COLOUR_FALLBACK);
  };

  const handleNewColorCreate = async () => {
    if (!newColorName.trim()) {
      return;
    }

    // Parse group and color name from the input
    const parseColorName = (fullName: string) => {
      // Check for slash format (group/color) - only this creates grouped colors
      const slashParts = fullName.split("/");
      if (slashParts.length === 2) {
        return {
          groupName: slashParts[0].trim(),
          colorName: slashParts[1].trim(),
        };
      }

      // Everything else (including dash-separated names) is treated as ungrouped
      return {
        // For ungrouped colors, use the color name as the group name
        // The backend will recognize this as ungrouped when groupName === colorName
        groupName: newColorGroupName || fullName.trim(),
        colorName: fullName.trim(),
      };
    };

    const { groupName, colorName } = parseColorName(newColorName);

    if (!groupName || !colorName) {
      return;
    }

    // Check for duplicate colors
    const { exists, location } = checkColorExists(
      colorName,
      groupName === colorName ? null : groupName, // null for ungrouped colors
      mergedColorGroups,
      mergedColors,
    );

    if (exists) {
      toast.error(`Color "${colorName}" already exists in ${location}`);
      return;
    }

    // Check for ungrouped color merge conflicts
    const isUngroupedColor = groupName === colorName;
    if (isUngroupedColor) {
      const { willMerge, existingGroup } = checkUngroupedMergeConflict(
        colorName,
        mergedColorGroups,
      );

      if (willMerge && existingGroup) {
        // Show informative toast about the merge behavior
        toast.info(
          `Color "${colorName}" will be added to the existing "${existingGroup.name}" group (${existingGroup.colors.length} colors) as "${colorName}/${colorName}"`,
        );
      }
    }

    try {
      // Make API call - this will auto-create the group if it doesn't exist
      await addColor(groupName, colorName, newColorValue);

      // Expand the group so user can see the new color
      expandGroup(groupName);

      closeNewColorPopover();
    } catch (error) {
      console.error("Failed to create color:", error);
      toast.error("Failed to create color. Please try again.");
    }
  };

  const handleNewColorPopoverKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      closeNewColorPopover();
    }
  };

  // Early returns for loading states
  const shouldShowSkeleton = !cssCustomProperties;
  const hasLightColors = useMemo(
    () =>
      lightColors?.length > 0 ||
      lightColorGroups?.some((g) => g.colors.length > 0),
    [lightColors, lightColorGroups],
  );

  // Prepare display state
  const hasNoDarkColors = !darkColors?.length && !darkColorGroups?.length;

  const shouldShowDarkModeGenerate =
    isCanvasDarkMode && hasNoDarkColors && hasLightColors;
  const shouldShowTryAgain = !hasAnyColors && shouldShowTryAgainState;

  // Filter colors for search
  const filteredUngroupedColors = ungroupedColors?.filter((color) => {
    if (!searchQuery) return true;
    return color.name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredColorGroups = colorGroups?.filter((group) => {
    if (!searchQuery) return true;
    return (
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.colors.some((color) =>
        color.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    );
  });

  const hasSearchResults =
    filteredUngroupedColors.length > 0 || filteredColorGroups.length > 0;

  if (shouldShowSkeleton) {
    return (
      <DesignTokensSkeleton
        showHeader={true}
        collapseRight={collapseRight}
        panelRef={panelRef}
      />
    );
  }
  return (
    <div
      ref={panelRef}
      className={cn(
        "w-full h-full bg-background-primary border-r border-border-primary flex flex-col relative",
        className,
      )}
      id="design-tokens-panel-root"
    >
      <DesignTokensPanelHeader collapseRight={collapseRight} />

      {/* Tab Navigation  TODO: add text styles tab*/}
      {/* <div className="p-2 border-b border-[#2f3035]">
        <ToggleGroup
          type="single"
          value={activeTab}
          onValueChange={(value) =>
            value && setActiveTab(value as "colors" | "text-styles")
          }
          className="bg-[#1f1f22] rounded-md p-0.5 w-full"
        >
          <ToggleGroupItem
            value="colors"
            className="flex-1 px-2 py-1 text-xs font-medium rounded-sm"
          >
            Colors
          </ToggleGroupItem>
          <ToggleGroupItem
            value="text-styles"
            className="flex-1 px-2 py-1 text-xs font-medium rounded-sm"
          >
            Text styles
          </ToggleGroupItem>
        </ToggleGroup>
      </div> */}

      {/* Search and Controls */}
      <div className="p-2 border-b border-[#2f3035] flex items-center gap-2 max-h-11 min-w-0">
        <SearchInput
          value={designTokensSearchQuery}
          onChange={(e) => setDesignTokensSearchQuery(e.target.value)}
          className="h-7 flex-1 min-w-0"
          placeholder="Search"
          onClear={() => setDesignTokensSearchQuery("")}
        />

        <Button
          variant="ghost"
          size="sm"
          className="p-0 hover:bg-[#2f3035] min-w-7 min-h-7 shrink-0"
          onClick={addNewColor}
          aria-label="Add new color"
        >
          <Plus
            className="text-[#808086] w-4 h-4"
            data-tooltip-id="tooltip"
            data-tooltip-content="Add new token"
          />
        </Button>
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto hide-scrollbar p-2 min-w-0 max-w-full"
        style={{ boxSizing: "border-box" }}
      >
        {activeTab === "colors" && (
          <ColorsTabContent
            hasAnyColors={hasAnyColors}
            shouldShowDarkModeGenerate={shouldShowDarkModeGenerate}
            shouldShowTryAgain={shouldShowTryAgain}
            searchQuery={searchQuery}
            hasSearchResults={hasSearchResults}
            loading={loading}
            filteredUngroupedColors={filteredUngroupedColors}
            filteredColorGroups={filteredColorGroups}
            getDisplayColor={getDisplayColor}
            cssCustomProperties={cssCustomProperties}
            expandedGroups={expandedGroups}
            colorGroupEditing={colorGroupEditing}
            colorInputRefMap={colorInputRefMap}
            onRefreshColors={refreshColors}
            onOpenEditPopover={openEditPopover}
            onDeleteColor={deleteColor}
            onToggleGroup={toggleGroup}
            onHandleRenameGroup={handleRenameGroup}
            onHandleDeleteGroup={handleDeleteGroup}
            onAddColorToGroup={addColorToGroup}
            onSaveColorName={saveColorName}
            onSaveGroupName={saveGroupName}
          />
        )}

        {activeTab === "text-styles" && (
          <div className="p-4 text-center">
            <div className="text-[#808086] text-sm">
              Text styles functionality coming soon.
            </div>
          </div>
        )}
      </div>

      {/* EditColorPopover */}
      {isEditPopoverOpen && (
        <EditColorPopover
          isOpen={isEditPopoverOpen}
          selectedColor={selectedColorForEdit}
          selectedColorId={selectedColorId}
          editingColorInPicker={editingColorName}
          lightColorToken={lightColorToken}
          darkColorToken={darkColorToken}
          onClose={closeEditPopover}
          onNameChange={handleColorNameChange}
          onColorChange={handleColorValueChange}
          onSaveNameFromPicker={handleSaveColorFromPicker}
          onKeyDown={handlePopoverKeyDown}
          anchorRef={panelRef}
          mousePosition={mousePosition}
        />
      )}

      {/* NewColorPopover */}
      {!loading && (
        <NewColorPopover
          isOpen={isNewColorPopoverOpen}
          newColorName={newColorName}
          newColorValue={newColorValue}
          onClose={closeNewColorPopover}
          onNameChange={setNewColorName}
          onValueChange={setNewColorValue}
          onCreate={handleNewColorCreate}
          onKeyDown={handleNewColorPopoverKeyDown}
          anchorRef={panelRef}
        />
      )}
    </div>
  );
};

interface DesignTokenColorItemProps {
  color: ColorToken;
  getDisplayColor: (
    color: ColorToken,
    cssCustomProperties?: {
      root: Record<string, string>;
      dark: Record<string, string>;
    } | null,
    comboboxOptions?: Array<{ tailwind: string; css: string }>,
  ) => string;
  cssCustomProperties?: {
    root: Record<string, string>;
    dark: Record<string, string>;
  } | null;
  onEdit: (color: ColorToken, event?: React.MouseEvent) => void;
  onDelete: (groupName: string, colorName: string) => Promise<void>;
  editingColorNameId: string | null;
  tempColorName: string;
  onStartEditingColorName: (colorPath: string, currentName: string) => void;
  onCancelEditingColorName: () => void;
  onSaveColorName: () => void;
  onColorNameChange: (name: string) => void;
  onColorNameEditKeyDown: (e: React.KeyboardEvent) => void;
  colorInputRefMap: React.MutableRefObject<Map<string, HTMLInputElement>>;
}

const DesignTokenColorItem: React.FC<DesignTokenColorItemProps> = React.memo(
  ({
    color,
    getDisplayColor,
    cssCustomProperties,
    onEdit,
    onDelete,
    editingColorNameId,
    tempColorName,
    onStartEditingColorName,
    onSaveColorName,
    onColorNameChange,
    onColorNameEditKeyDown,
    colorInputRefMap,
  }) => {
    const displayColor = useMemo(() => {
      return getDisplayColor(color, cssCustomProperties);
    }, [color, cssCustomProperties, getDisplayColor]);

    const handleEdit = (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit(color, e);
    };

    const handleDelete = async () => {
      try {
        await onDelete(color.group || "", color.name);
      } catch (error) {
        console.error("Failed to delete color:", error);
        toast.error("Failed to delete color. Please try again.");
      }
    };

    // Determine if this is an ungrouped color (no group or empty group)
    const isUngrouped = !color.group || color.group === "";
    const isProtected = isUngrouped && isProtectedColorClass(color.name);
    const canEdit = !isProtected && editingColorNameId !== color.tailwindClass;

    const handleRename = () => {
      if (canEdit) {
        onStartEditingColorName(color.tailwindClass, color.name);
      }
    };

    return (
      <ColorItemContextMenu
        color={color}
        onRename={handleRename}
        onDelete={handleDelete}
        isProtected={isProtected}
        protectionReason={getProtectionReason(color.name)}
      >
        <div
          className="flex items-center px-2 py-1 rounded-md hover:bg-[#2f3035] group w-full capitalize"
          onDoubleClick={() => {
            if (canEdit) {
              onStartEditingColorName(color.tailwindClass, color.name);
            }
          }}
        >
          <div className="flex items-center gap-2 w-full">
            <div className="w-4 h-4 p-0.5 flex items-center justify-center relative">
              {color.isFallback ? (
                // Show transparent swatch with question mark for fallback tokens
                <div className="w-full h-full relative">
                  <ColorSwatch color="transparent" className="opacity-30" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-text-secondary">
                      ?
                    </span>
                  </div>
                </div>
              ) : (
                <ColorSwatch color={displayColor} />
              )}
            </div>
            <div className="flex items-center gap-1 w-full min-w-0">
              {editingColorNameId === color.tailwindClass ? (
                <Input
                  ref={(el) => {
                    if (el) {
                      colorInputRefMap.current.set(color.tailwindClass, el);
                    } else {
                      colorInputRefMap.current.delete(color.tailwindClass);
                    }
                  }}
                  value={tempColorName}
                  onChange={(e) => onColorNameChange(e.target.value)}
                  onKeyDown={onColorNameEditKeyDown}
                  onBlur={onSaveColorName}
                  className="flex-1 h-5 px-1 py-0 text-xs bg-[#1f1f22] border border-[#6448ef] text-[#f5f5f6] focus:outline-hidden focus:border-[#6448ef] min-w-0"
                  autoFocus
                  disabled={isProtected}
                />
              ) : (
                <span
                  className={`text-xs font-normal truncate flex-1 min-w-0 ${
                    isProtected
                      ? "cursor-not-allowed opacity-60"
                      : "cursor-pointer hover:text-text-primary"
                  } text-[#f5f5f6]`}
                  data-tooltip-id="tooltip"
                  data-tooltip-content={
                    isProtectedColorClass(color.name)
                      ? getProtectionReason(color.name)
                      : color.isFallback
                        ? `This token doesn't exist in the current mode. Click to edit or create it.`
                        : null
                  }
                >
                  {color.name}
                </span>
              )}
              <div className="flex items-center gap-1 shrink-0">
                {!displayColor.startsWith("#") ? (
                  <CircleAlert
                    className="max-w-4 max-h-4 min-w-4 min-h-4 text-icons-alert"
                    data-tooltip-id="tooltip"
                    data-tooltip-content={`Tailwind token couldn’t be parsed. Please check if "${color.value}" is a valid color.`}
                  />
                ) : (
                  <span
                    className={"text-xs font-normal uppercase text-[#808086]"}
                  >
                    {color.isFallback ? "-" : displayColor}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 hover:bg-[#2f3035] bg-background-tertiary shrink-0"
                  onClick={handleEdit}
                  aria-label="Edit color"
                >
                  <Settings2
                    className="text-icon-primary w-3 h-3"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Edit"
                  />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </ColorItemContextMenu>
    );
  },
);

interface DesignTokenColorGroupProps {
  group: ColorStoreGroup;
  getDisplayColor: (
    color: ColorToken,
    cssCustomProperties?: {
      root: Record<string, string>;
      dark: Record<string, string>;
    } | null,
    comboboxOptions?: Array<{ tailwind: string; css: string }>,
  ) => string;
  cssCustomProperties?: {
    root: Record<string, string>;
    dark: Record<string, string>;
  } | null;
  expandedGroups: Set<string>;
  toggleGroup: (groupName: string) => void;
  handleRenameGroup: (oldName: string, newName: string) => void;
  handleDeleteGroup: (groupName: string) => void;
  onEditColor: (color: ColorToken, event?: React.MouseEvent) => void;
  onDeleteColor: (groupName: string, colorName: string) => Promise<void>;
  onAddColor: (groupName: string) => void;
  editingGroupName: string | null;
  tempGroupName: string;
  onStartEditingGroup: (groupName: string) => void;
  onCancelEditingGroup: () => void;
  onSaveGroupName: () => void;
  onGroupNameChange: (name: string) => void;
  onGroupNameKeyDown: (e: React.KeyboardEvent) => void;
  addingColorToGroup: string | null;
  newColorName: string;
  editingColorNameId: string | null;
  tempColorName: string;
  onStartEditingColorName: (colorPath: string, currentName: string) => void;
  onCancelEditingColorName: () => void;
  onSaveColorName: () => void;
  onColorNameChange: (name: string) => void;
  onColorNameEditKeyDown: (e: React.KeyboardEvent) => void;
  colorInputRefMap: React.MutableRefObject<Map<string, HTMLInputElement>>;
}

const DesignTokenColorGroup: React.FC<DesignTokenColorGroupProps> = React.memo(
  ({
    group,
    getDisplayColor,
    cssCustomProperties,
    expandedGroups,
    toggleGroup,
    handleDeleteGroup,
    onEditColor,
    onDeleteColor,
    onAddColor,
    editingGroupName,
    tempGroupName,
    onStartEditingGroup,
    onSaveGroupName,
    onGroupNameChange,
    onGroupNameKeyDown,
    addingColorToGroup,
    newColorName,
    editingColorNameId,
    tempColorName,
    onStartEditingColorName,
    onCancelEditingColorName,
    onSaveColorName,
    onColorNameChange,
    onColorNameEditKeyDown,
    colorInputRefMap,
  }) => {
    if (group.colors.length === 0) return null;

    const isExpanded = expandedGroups.has(group.name);
    const isEditing = editingGroupName === group.name;

    const handleChevronClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      if (!isEditing) {
        toggleGroup(group.name);
      }
    };

    const handleDoubleClick = () => {
      onStartEditingGroup(group.name);
    };

    return (
      <div className="flex flex-col w-full">
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div
              className="flex items-center p-2 rounded-md group hover:bg-[#2a2a2e] cursor-pointer transition-colors text-left h-7 capitalize w-full"
              onDoubleClick={handleDoubleClick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleChevronClick(e);
                }
              }}
              aria-expanded={isExpanded}
            >
              <div className="flex items-center gap-1.5 h-[20px] flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-[#f5f5f6] text-xs font-normal hover:text-text-primary transition-colors shrink-0">
                  <div
                    className="transition-transform ease-in-out cursor-pointer hover:bg-[#2f3035] rounded p-0.5 border-0 bg-transparent"
                    onClick={handleChevronClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleChevronClick(e);
                      }
                    }}
                    aria-label={`${isExpanded ? "Collapse" : "Expand"} ${
                      group.name
                    } group`}
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-[#606064]" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-[#606064]" />
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0 h-7 flex items-center">
                  {isEditing ? (
                    <Input
                      data-editing-group={group.name}
                      value={tempGroupName}
                      onChange={(e) => onGroupNameChange(e.target.value)}
                      onKeyDown={onGroupNameKeyDown}
                      onBlur={onSaveGroupName}
                      className="w-full h-7 px-1 py-0 text-xs bg-[#1f1f22] border border-[#6448ef] text-[#f5f5f6] focus:outline-hidden focus:border-[#6448ef] min-w-0"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                      disabled={false}
                    />
                  ) : (
                    <span
                      className="w-full h-7 px-1 text-[#f5f5f6] text-xs font-normal transition-colors truncate flex items-center hover:text-text-primary cursor-pointer"
                      title=""
                    >
                      {group.name}
                    </span>
                  )}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-5 h-5 p-0 hover:bg-[#2f3035] opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddColor(group.name);
                }}
                title="Add new token to group"
              >
                <Plus
                  className="text-[#808086] w-4 h-4"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Add new token to group"
                />
              </Button>
            </div>
          </ContextMenuTrigger>

          <ContextMenuContent
            className="bg-[#1f1f22] border-[#2f3035] text-white"
            onCloseAutoFocus={(e) => {
              e.preventDefault();
            }}
          >
            <ContextMenuItem
              className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
              onClick={() => {
                onStartEditingGroup(group.name);
              }}
              disabled={false}
            >
              <Edit2 className="w-3 h-3 mr-2" />
              Rename
            </ContextMenuItem>
            <ContextMenuItem
              className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
              onClick={() => onAddColor(group.name)}
            >
              <Plus className="w-3 h-3 mr-2" />
              Add Color
            </ContextMenuItem>
            <ContextMenuSeparator className="bg-[#2f3035]" />
            <ContextMenuItem
              className="text-xs text-destructive hover:bg-[#2f3035] focus:bg-[#2f3035]"
              onClick={() => {
                handleDeleteGroup(group.name);
              }}
              disabled={false}
            >
              <Trash2 className="w-3 h-3 mr-2" />
              Delete
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>

        <div
          className={`pl-4 overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="pl-2 border-l border-[#2f3035] flex flex-col">
            {group.colors.map((color) => (
              <DesignTokenColorItem
                key={color.tailwindClass}
                color={color}
                getDisplayColor={getDisplayColor}
                cssCustomProperties={cssCustomProperties}
                onEdit={onEditColor}
                onDelete={onDeleteColor}
                editingColorNameId={editingColorNameId}
                tempColorName={tempColorName}
                onStartEditingColorName={onStartEditingColorName}
                onCancelEditingColorName={onCancelEditingColorName}
                onSaveColorName={onSaveColorName}
                onColorNameChange={onColorNameChange}
                onColorNameEditKeyDown={onColorNameEditKeyDown}
                colorInputRefMap={colorInputRefMap}
              />
            ))}

            {/* Add new color input */}
            {addingColorToGroup === group.name && (
              <div className="flex items-center px-2 py-1 rounded-md hover:bg-[#2f3035] group w-full">
                <div className="flex items-center gap-2 w-full">
                  <div className="w-4 h-4 p-0.5 flex items-center justify-center">
                    <ColorSwatch color={COLOUR_FALLBACK} />
                  </div>
                  <div className="flex items-center justify-between gap-1 w-full">
                    <Input
                      value={newColorName}
                      className="flex-1 h-5 px-1 py-0 text-xs bg-[#1f1f22] border border-[#6448ef] text-[#f5f5f6] focus:outline-hidden focus:border-[#6448ef]"
                      placeholder="Color name"
                      autoFocus
                    />

                    <span className="text-[#808086] text-xs font-normal uppercase">
                      {COLOUR_FALLBACK}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

export default React.memo(DesignTokensPanel);
